/**
 * @voltxampere/ui — DirtyTracker (Sprint 12)
 *
 * Per Blueprint §12.2. Accumulates per-frame redraw regions; once
 * the queue grows past `MAX_REGIONS`, the tracker flips to a
 * full-redraw flag because pixel-perfect clip + draw work for
 * 20+ small regions costs more than one fullscreen `clearRect`
 * pass.
 *
 * The 20-region threshold is empirical, not load-bearing. Sprint
 * 20 chaos benchmark will tune it; until then it lives here as a
 * constant so unit tests can pin the boundary.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export const MAX_REGIONS = 20;
export class DirtyTracker {
    regions = [];
    fullRedrawRequested = false;
    /**
     * Add a screen-space region to the redraw queue. Once full
     * redraw is already requested, additional regions are folded
     * silently — they would all be covered by the upcoming
     * fullscreen pass.
     */
    markDirty(region) {
        if (this.fullRedrawRequested)
            return;
        this.regions.push(region);
        if (this.regions.length > MAX_REGIONS) {
            this.fullRedrawRequested = true;
            this.regions = [];
        }
    }
    /**
     * Force the next consume to report a full redraw. Used by pan,
     * zoom, theme switch, viewport reset — anything that touches
     * every pixel.
     */
    markAllDirty() {
        this.fullRedrawRequested = true;
        this.regions = [];
    }
    /**
     * Hand the queue to the renderer and reset internal state.
     * Single consumer model — call this once per frame.
     */
    consume() {
        const result = {
            full: this.fullRedrawRequested,
            regions: this.regions,
        };
        this.regions = [];
        this.fullRedrawRequested = false;
        return result;
    }
    /** True when nothing is pending. The renderer skips its frame. */
    get isClean() {
        return !this.fullRedrawRequested && this.regions.length === 0;
    }
    /** Internal-state inspector for tests; not used by the renderer. */
    get pendingCount() {
        return this.fullRedrawRequested ? -1 : this.regions.length;
    }
}
//# sourceMappingURL=dirty.js.map